import React from "react";
import { getCurrencySymbol } from "../../../../../tenant/currency";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
	BorrowEligibilityData,
	BuyElectricityConfirmationData,
	PaymentOptions,
} from "../../buy-electricity.types";
import PaymentMethodModal, {
	PaymentMethodKey,
	PaymentMethodRow,
	RecapRow,
} from "../../../components/payment-method-modal/PaymentMethodModal";

interface BuyElectricityConfirmationProps {
	data: BuyElectricityConfirmationData;
	totalAmount: number;
	onClose: () => void;
	onPay: (paymentMethod: PaymentOptions) => void;
	availableMethods: PaymentOptions[];
	isBorrowTokenDisabled?: boolean;
	borrowEligibility?: BorrowEligibilityData | null;
	walletAvailableBalance?: number;
}

const currencySymbol = (code: string): string => getCurrencySymbol(code);

const fmt = (value: number, decimals = 2) =>
	value.toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

const KEY_TO_OPTION: Record<PaymentMethodKey, PaymentOptions> = {
	card: PaymentOptions.Card,
	wallet: PaymentOptions.Wallet,
	transfer: PaymentOptions.Transfer,
	borrow: PaymentOptions.BorrowToken,
	askAFriend: PaymentOptions.AskAFriendToPay,
};

export const BuyElectricityConfirmation: React.FC<BuyElectricityConfirmationProps> = ({
	data,
	totalAmount,
	onClose,
	onPay,
	availableMethods,
	isBorrowTokenDisabled = false,
	borrowEligibility,
	walletAvailableBalance = 0,
}) => {
	const { t } = useTranslation();
	const sym = currencySymbol(data.currency);
	const outstandingDebt = data.outstandingDebt ?? data.outstandingBalance ?? 0;
	const hasDebt = outstandingDebt > 0;

	const recap: RecapRow[] = [
		// { label: "Type", value: "Electricity" },
		// ...(data.disco ? [{ label: "Disco", value: data.disco }] : []),
		{ label: t("common.labels.meterType"), value: data.paymentType === "prepaid" ? t("common.labels.prepaid") : t("common.labels.postpaid") },
		{ label: t("common.labels.meterNumber"), value: data.meterNumber },
		// ...(data.customerName ? [{ label: "Meter Name", value: data.customerName }] : []),
		// { label: "Meter Address", value: data.meterAddress || "N/A" },
		// ...(data.phoneNumber ? [{ label: "Phone number", value: data.phoneNumber }] : []),
		// ...(data.fee !== undefined && data.fee > 0 ? [{ label: "Fee", value: `${sym}${fmt(data.fee)}` }] : []),
	];

	const methods: PaymentMethodRow[] = [];
	if (availableMethods.includes(PaymentOptions.Card)) methods.push({ key: "card" });
	if (availableMethods.includes(PaymentOptions.Wallet)) {
		methods.push({ key: "wallet", pill: { label: t("buyElectricity.web.balanceLabel"), amount: `${sym}${fmt(walletAvailableBalance, 0)}` } });
	}
	if (availableMethods.includes(PaymentOptions.Transfer)) methods.push({ key: "transfer" });
	if (availableMethods.includes(PaymentOptions.AskAFriendToPay)) methods.push({ key: "askAFriend" });
	// Borrow Units payment option hidden on the payment page
	// if (availableMethods.includes(PaymentOptions.BorrowToken)) {
	// 	methods.push({
	// 		key: "borrow",
	// 		pill: { label: "Max Limit:", amount: `${sym}${fmt(borrowEligibility?.maximumBorrowAmount ?? 0, 0)}` },
	// 		disabled: isBorrowTokenDisabled,
	// 	});
	// }

	const handlePay = (key: PaymentMethodKey) => {
		const method = KEY_TO_OPTION[key];
		if (method === PaymentOptions.Wallet && totalAmount > walletAvailableBalance) {
			const shortfall = totalAmount - walletAvailableBalance;
			toast.error(t("buyElectricity.web.insufficientBalanceNeedMore", { amount: `${sym}${fmt(shortfall)}` }));
			return;
		}
		onPay(method);
	};

	return (
		<PaymentMethodModal
			title={t("common.labels.payment")}
			amountText={`${sym}${fmt(totalAmount, 0)}`}
			debtText={hasDebt ? `${sym}${fmt(outstandingDebt)}` : undefined}
			recap={recap}
			methods={methods}
			payLabel={t("borrowToken.payAmount", { amount: `${sym}${fmt(totalAmount, 0)}` })}
			onClose={onClose}
			onPay={handlePay}
			onDisabledSelect={key => {
				if (key === "borrow") {
					toast.error(t("buyElectricity.web.outstandingDebtSettleBeforeBorrow"));
				}
			}}
		/>
	);
};
